import { type TokenResponse, useGoogleLogin } from "@react-oauth/google"
import { Button, Flex } from "antd"
import React, { Fragment } from "react"
import { useTranslation } from "react-i18next"

import Images from "../../../Images"
import { showNotification } from "../../../utils/notification/show-notification"
import { AppleSocialProviders } from "./AppleSocialProviders"

const SOCIAL_PROVIDERS = {
  google: {
    name: "Google",
    icon: Images.google,
    buttonText: "Continue with Google"
  },
  apple: {
    name: "Apple",
    icon: Images.apple,
    buttonText: "Continue with Apple"
  }
  // facebook: {
  //   name: "Facebook",
  //   icon: Images.facebook,
  //   buttonText: "Continue with Facebook"
  // },
}

interface SocialProvidersProps {
  providers: string[]
  socialAuth: (data: Record<string, unknown>) => void
  loading: boolean
}

function SocialProviders({
  providers = [],
  socialAuth,
  loading = false
}: SocialProvidersProps) {
  const { t } = useTranslation()

  const createProviderHandler = (providerName: string) => {
    const handleSuccess = (
      response: Omit<TokenResponse, "error" | "error_description" | "error_uri">
    ) => {
      if (response && response?.access_token) {
        socialAuth({ token: response?.access_token, provider: providerName })
      }
    }

    const handleFailure = (response: any) => {
      showNotification.error({
        message: `${providerName} auth failed`,
        description: response?.error?.message
      })
      console.error(`${providerName} auth failed:`, response)
    }

    return { handleSuccess, handleFailure }
  }

  const googleHandler = createProviderHandler("google")

  const responseGoogle = useGoogleLogin({
    onSuccess: googleHandler.handleSuccess,
    onError: googleHandler.handleFailure
  })

  const handleSocialLogin = (providerName: string) => {
    switch (providerName) {
      case "google":
        responseGoogle()
        break
      case "facebook":
        // responseFacebook()
        console.log("Facebook login not implemented yet")
        break
      case "apple":
        break
      default:
        console.warn(`Unknown provider: ${providerName}`)
    }
  }

  const renderProvider = (providerKey: string) => {
    const provider =
      SOCIAL_PROVIDERS[providerKey as keyof typeof SOCIAL_PROVIDERS]

    if (!provider) {
      console.warn(`Provider ${providerKey} not found in SOCIAL_PROVIDERS`)
      return null
    }

    if (providerKey === "apple") return <AppleSocialProviders />

    return (
      <Button
        key={providerKey}
        onClick={() => handleSocialLogin(providerKey)}
        size="large"
        className="social-provider-button"
        disabled={loading}
      >
        <div className="flex items-center gap-3 justify-center">
          <img
            src={provider.icon}
            alt={`${provider.name}-icon`}
            className="w-5 h-5"
          />
          <span>{t(provider.buttonText)}</span>
        </div>
      </Button>
    )
  }

  if (!providers.length) return null

  return (
    <Flex vertical gap="small">
      {providers.map((providerKey) => (
        <Fragment key={providerKey}>{renderProvider(providerKey)}</Fragment>
      ))}
    </Flex>
  )
}

export default SocialProviders
